import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import {
  ApiUserIdFormat,
  type OutboundWebhookEventType,
  WebhookPayloadShape,
  WebhookTimestampMode,
} from "../../../generated/graphql";
import type { EventTypeOptionsState } from "../hooks/use-event-type-options";

const styles = {
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 0.5,
  } satisfies SxProps<Theme>,
};

const PAYLOAD_SHAPE_OPTIONS = Object.values(WebhookPayloadShape);
const ID_FORMAT_OPTIONS = Object.values(ApiUserIdFormat);

type EventTypeMenuItem = {
  eventType: OutboundWebhookEventType;
  deprecated: boolean;
  outOfScope: boolean;
};

const eventTypeSecondaryText = (
  item: EventTypeMenuItem,
): string | undefined => {
  if (item.outOfScope) {
    return "Not delivered with these settings";
  }
  return item.deprecated ? "Deprecated" : undefined;
};

/**
 * Option names stay short so the select never truncates them; what the
 * selected mode does is one line of helper text under the field. The
 * per-event table is in docs/codebase-library/outbound-webhook-timestamp-format.md.
 */
const TIMESTAMP_MODE_LABELS: Record<WebhookTimestampMode, string> = {
  [WebhookTimestampMode.Utc]: "UTC",
  [WebhookTimestampMode.EventLocal]: "Event location",
  [WebhookTimestampMode.StopLocal]: "Stop local (legacy)",
};

const TIMESTAMP_MODE_HELP: Record<WebhookTimestampMode, string> = {
  [WebhookTimestampMode.Utc]: "Every time in UTC, ending in Z.",
  [WebhookTimestampMode.EventLocal]:
    "Times where the event happened (stop or terminal), with offset; createdAt and uploads in UTC; invoices in carrier time.",
  [WebhookTimestampMode.StopLocal]:
    "Order status at the stop, the rest in carrier time; terminal-local times moves other stop events to the stop.",
};

const TIMESTAMP_MODE_OPTIONS: readonly WebhookTimestampMode[] = [
  WebhookTimestampMode.Utc,
  WebhookTimestampMode.EventLocal,
  WebhookTimestampMode.StopLocal,
];

type AdvancedApiKeyFieldsProps = {
  readonly payloadShapes: WebhookPayloadShape[];
  readonly eventTypes: OutboundWebhookEventType[];
  readonly idFormat: ApiUserIdFormat;
  readonly customMapping: boolean;
  readonly emitTerminalHandoffEvents: boolean;
  readonly webhookTimestampMode: WebhookTimestampMode;
  /**
   * The event types this key can receive with its current shapes and opt-in,
   * from `useEventTypeOptions`. The picker offers only these, and flags any
   * selected type outside them in the error colour.
   */
  readonly eventTypeOptions: EventTypeOptionsState;
  readonly onPayloadShapesChange: (value: WebhookPayloadShape[]) => void;
  readonly onEventTypesChange: (value: OutboundWebhookEventType[]) => void;
  readonly onIdFormatChange: (value: ApiUserIdFormat) => void;
  readonly onCustomMappingChange: (value: boolean) => void;
  readonly onEmitTerminalHandoffEventsChange: (value: boolean) => void;
  readonly onWebhookTimestampModeChange: (value: WebhookTimestampMode) => void;
};

/**
 * Shared form controls for the advanced webhook-configuration fields of an API
 * key: the payload-shape multi-select, the event-type multi-select, the
 * id-format select, and the custom-mapping toggle. Used by both the create and
 * edit dialogs so the two forms cannot drift apart.
 *
 * The component is fully controlled — it renders the values passed in and
 * reports changes through the `on*Change` callbacks; it holds no state itself.
 */
const AdvancedApiKeyFields = ({
  payloadShapes,
  eventTypes,
  idFormat,
  customMapping,
  emitTerminalHandoffEvents,
  webhookTimestampMode,
  eventTypeOptions,
  onPayloadShapesChange,
  onEventTypesChange,
  onIdFormatChange,
  onCustomMappingChange,
  onEmitTerminalHandoffEventsChange,
  onWebhookTimestampModeChange,
}: AdvancedApiKeyFieldsProps) => {
  const handlePayloadShapesChange = useCallback(
    (event: SelectChangeEvent<WebhookPayloadShape[]>) => {
      const { value } = event.target;
      const next =
        typeof value === "string"
          ? (value.split(",") as WebhookPayloadShape[])
          : value;
      onPayloadShapesChange(next);
    },
    [onPayloadShapesChange],
  );

  const removePayloadShape = useCallback(
    (shape: WebhookPayloadShape) => {
      onPayloadShapesChange(payloadShapes.filter((s) => s !== shape));
    },
    [onPayloadShapesChange, payloadShapes],
  );

  const handleEventTypesChange = useCallback(
    (event: SelectChangeEvent<OutboundWebhookEventType[]>) => {
      const { value } = event.target;
      const next =
        typeof value === "string"
          ? (value.split(",") as OutboundWebhookEventType[])
          : value;
      onEventTypesChange(next);
    },
    [onEventTypesChange],
  );

  const removeEventType = useCallback(
    (eventType: OutboundWebhookEventType) => {
      onEventTypesChange(eventTypes.filter((t) => t !== eventType));
    },
    [onEventTypesChange, eventTypes],
  );

  // Out-of-scope selections stay in the menu, checked, so the admin can
  // uncheck them there as well as from the chip.
  const eventTypeMenuItems = useMemo((): EventTypeMenuItem[] => {
    if (eventTypeOptions.status !== "ready") {
      return eventTypes.map((eventType) => ({
        eventType,
        deprecated: false,
        outOfScope: false,
      }));
    }
    return [
      ...eventTypeOptions.options.map((option) => ({
        ...option,
        outOfScope: false,
      })),
      ...eventTypeOptions.outOfScope.map((eventType) => ({
        eventType,
        deprecated: false,
        outOfScope: true,
      })),
    ];
  }, [eventTypeOptions, eventTypes]);

  const outOfScope = useMemo(
    () =>
      eventTypeOptions.status === "ready" ? eventTypeOptions.outOfScope : [],
    [eventTypeOptions],
  );
  const outOfScopeSet = useMemo(() => new Set(outOfScope), [outOfScope]);

  const renderPayloadShapeChips = useCallback(
    (selected: WebhookPayloadShape[]) => (
      <Box sx={styles.chips}>
        {selected.map((shape) => (
          <Chip
            key={shape}
            size="small"
            label={shape}
            onMouseDown={(e) => {
              // Prevent the Select from opening/handling the click so the
              // chip's delete icon can remove the shape.
              e.stopPropagation();
            }}
            onDelete={() => {
              removePayloadShape(shape);
            }}
          />
        ))}
      </Box>
    ),
    [removePayloadShape],
  );

  const renderEventTypeChips = useCallback(
    (selected: OutboundWebhookEventType[]) => (
      <Box sx={styles.chips}>
        {selected.map((eventType) => (
          <Chip
            key={eventType}
            size="small"
            label={eventType}
            color={outOfScopeSet.has(eventType) ? "error" : "default"}
            onMouseDown={(e) => {
              // Prevent the Select from opening/handling the click so the
              // chip's delete icon can remove the event type.
              e.stopPropagation();
            }}
            onDelete={() => {
              removeEventType(eventType);
            }}
          />
        ))}
      </Box>
    ),
    [outOfScopeSet, removeEventType],
  );

  return (
    <>
      <FormControl fullWidth size="small">
        <InputLabel id="api-key-payload-shapes-label">
          Payload shapes
        </InputLabel>
        <Select
          multiple
          labelId="api-key-payload-shapes-label"
          label="Payload shapes"
          value={payloadShapes}
          input={<OutlinedInput label="Payload shapes" />}
          renderValue={renderPayloadShapeChips}
          onChange={handlePayloadShapesChange}
        >
          {PAYLOAD_SHAPE_OPTIONS.map((shape) => (
            <MenuItem key={shape} value={shape}>
              <Checkbox checked={payloadShapes.includes(shape)} />
              <ListItemText primary={shape} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        fullWidth
        size="small"
        error={eventTypeOptions.status === "error" || outOfScope.length > 0}
      >
        <InputLabel id="api-key-event-types-label">Event types</InputLabel>
        <Select
          multiple
          labelId="api-key-event-types-label"
          label="Event types"
          value={eventTypes}
          disabled={eventTypeOptions.status !== "ready"}
          input={<OutlinedInput label="Event types" />}
          renderValue={renderEventTypeChips}
          onChange={handleEventTypesChange}
        >
          {eventTypeMenuItems.map((item) => (
            <MenuItem key={item.eventType} value={item.eventType}>
              <Checkbox checked={eventTypes.includes(item.eventType)} />
              <ListItemText
                primary={item.eventType}
                secondary={eventTypeSecondaryText(item)}
              />
            </MenuItem>
          ))}
        </Select>
        {eventTypeOptions.status === "loading" && (
          <FormHelperText>Loading event types…</FormHelperText>
        )}
        {eventTypeOptions.status === "error" && (
          <FormHelperText>
            Could not load event types: {eventTypeOptions.message}{" "}
            <Button size="small" onClick={eventTypeOptions.retry}>
              Retry
            </Button>
          </FormHelperText>
        )}
        {eventTypeOptions.status === "ready" && outOfScope.length > 0 && (
          <FormHelperText>
            This key can never receive {outOfScope.join(", ")}. Remove them, or
            change the payload shapes or the transfer / recovery toggle.
          </FormHelperText>
        )}
        {eventTypeOptions.status === "ready" && outOfScope.length === 0 && (
          <FormHelperText>
            Leave empty to send every event type the selected payload shapes
            carry. Listing types here narrows that set and never widens it.
          </FormHelperText>
        )}
      </FormControl>
      <FormControl fullWidth size="small">
        <InputLabel id="api-key-id-format-label">ID format</InputLabel>
        <Select
          labelId="api-key-id-format-label"
          label="ID format"
          value={idFormat}
          onChange={(e) => {
            onIdFormatChange(e.target.value as ApiUserIdFormat);
          }}
        >
          {ID_FORMAT_OPTIONS.map((format) => (
            <MenuItem key={format} value={format}>
              {format}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Switch
            checked={customMapping}
            onChange={(e) => {
              onCustomMappingChange(e.target.checked);
            }}
          />
        }
        label="Custom mapping"
      />
      <FormControlLabel
        control={
          <Switch
            checked={emitTerminalHandoffEvents}
            onChange={(e) => {
              onEmitTerminalHandoffEventsChange(e.target.checked);
            }}
          />
        }
        label="Emit transfer / recovery completion events"
      />
      <FormControl fullWidth size="small">
        <InputLabel id="api-key-timestamp-mode-label">
          Webhook timestamp mode
        </InputLabel>
        <Select
          labelId="api-key-timestamp-mode-label"
          label="Webhook timestamp mode"
          value={webhookTimestampMode}
          onChange={(e) => {
            onWebhookTimestampModeChange(
              e.target.value as WebhookTimestampMode,
            );
          }}
        >
          {TIMESTAMP_MODE_OPTIONS.map((mode) => (
            <MenuItem key={mode} value={mode}>
              {TIMESTAMP_MODE_LABELS[mode]}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {TIMESTAMP_MODE_HELP[webhookTimestampMode]}
        </FormHelperText>
      </FormControl>
    </>
  );
};

export default AdvancedApiKeyFields;
